import React, { useState, useEffect } from 'react';
import { FileText, Upload, Trash2, CheckCircle2, FileDown } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const ManageDocs = () => {
  const [docs, setDocs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [title, setTitle] = useState('');
  const [docFile, setDocFile] = useState<File | null>(null);

  // const backendUrl = "http://localhost/seva-backend";
  const backendUrl = "https://www.sevaafoundation.com/seva-backend";
  const token = localStorage.getItem("sevaa_admin_token");

  const fetchDocs = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/docs/list_docs.php`);
      const data = await res.json();
      if (data.success) setDocs(data.documents);
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchDocs(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!docFile) return toast.error("Please upload a file");
    
    setIsSubmitting(true);
    const loadingToast = toast.loading("Uploading document...");
    const submitData = new FormData();
    submitData.append("title", title);
    submitData.append("document", docFile);

    try {
      const response = await fetch(`${backendUrl}/api/docs/upload_doc.php`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: submitData
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Document uploaded!", { id: loadingToast });
        setTitle(''); setDocFile(null);
        fetchDocs();
      } else {
        toast.error(data.error || "Failed to upload", { id: loadingToast });
      }
    } catch (err) {
      toast.error("Server Error", { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this document permanently?")) return;
    try {
      const response = await fetch(`${backendUrl}/api/docs/delete_doc.php?id=${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Document deleted");
        setDocs(docs.filter(d => d.id !== id));
      } else {
        toast.error(data.error);
      }
    } catch (err) {
      toast.error("Deletion failed");
    }
  };

  return (
    <div className="space-y-8 animate-fade-in-up pb-10 max-w-6xl mx-auto">
      <Toaster position="top-right" />
      <div>
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3"><FileText className="text-orange-500" /> Legal & Certifications</h1>
        <p className="text-slate-500 mt-1">Upload NGO registration, 12A, 80G, CSR-1, and other trust documents.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* UPLOAD FORM */}
        <div className="lg:col-span-1 bg-white p-6 rounded-2xl shadow-sm border border-slate-100 h-fit">
          <h2 className="font-bold text-lg mb-4 text-slate-800 border-b pb-2">Upload Document</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Document Title</label>
              <input required type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" placeholder="e.g. 12A Certificate" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">File (PDF or Image)</label>
              <div className="relative border-2 border-dashed border-slate-200 bg-slate-50 hover:border-orange-400 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer">
                <Upload className="text-slate-400" size={32} />
                <p className="text-xs text-slate-500 mt-2 font-medium text-center">{docFile ? docFile.name : 'Click to select file'}</p>
                <input required type="file" accept=".pdf,image/*" onChange={e => e.target.files && setDocFile(e.target.files[0])} className="absolute inset-0 opacity-0 cursor-pointer" />
              </div>
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl transition-all flex justify-center gap-2">
              <CheckCircle2 size={20} /> {isSubmitting ? 'Uploading...' : 'Upload File'}
            </button>
          </form>
        </div>

        {/* DOCS LIST */}
        <div className="lg:col-span-2">
          {isLoading ? <div className="p-8 text-center text-slate-500">Loading documents...</div> : (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-slate-500 text-xs font-bold uppercase">
                    <th className="px-6 py-4">Document Title</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {docs.length === 0 && <tr><td colSpan={2} className="p-8 text-center text-slate-500">No documents uploaded.</td></tr>}
                  {docs.map(doc => (
                    <tr key={doc.id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center"><FileText size={20} /></div>
                          <div>
                            <p className="font-bold text-slate-800">{doc.title}</p>
                            <p className="text-xs text-slate-400">Added on: {new Date(doc.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <a href={`${backendUrl}${doc.file_url}`} target="_blank" rel="noreferrer" className="p-2 bg-slate-100 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors" title="View Document">
                            <FileDown size={18} />
                          </a>
                          <button onClick={() => handleDelete(doc.id)} className="p-2 bg-slate-100 hover:bg-red-100 text-red-500 rounded-lg transition-colors" title="Delete">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageDocs;